import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  CohortNavTemplate,
  ProgramNavTemplate,
  QuizOutput,
} from '@efdigital/ll-addon-content';
import QuizInput from '@efdigital/ll-addon-content/components/quiz/quiz-input';
import AdminLegacyWarning from '@efdigital/ll-admin-v2/components/admin-legacy-warning.gts';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { programTemplateParts } from '@efdigital/ll-admin-v2/components/parts.gts';
import ContentBreadcrumb from '@efdigital/ll-admin-v2/components/programs/content-breadcrumb.gts';
import { EditQuizContext } from '@efdigital/ll-admin-v2/components/quizzes/edit-quiz-context.gts';
import { IsolatedQuizQuestion } from '@efdigital/ll-admin-v2/components/quizzes/isolated-quiz-question.gts';
import { QuizEditMode } from '@efdigital/ll-admin-v2/components/quizzes/quiz-edit-mode.gts';
import { QuizEditSidebar } from '@efdigital/ll-admin-v2/components/quizzes/quiz-edit-sidebar.gts';
import { Sidebar } from '@efdigital/ll-admin-v2/components/sidebar/sidebar.gts';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type CohortContentModuleQuizRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort/content/module/quiz';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { pageTitle } from 'ember-page-title';

interface ContentQuizSignature {
  Args: {
    model: ModelFrom<CohortContentModuleQuizRoute>;
    controller: unknown;
  };
}

const parts = programTemplateParts;

function eq(a: string, b: string): boolean {
  return a === b;
}

class ContentQuiz extends Component<ContentQuizSignature> {
  @service declare session: SessionService;
  @service declare store: Store;

  <template>
    {{pageTitle "Quiz"}}

    <ContentBreadcrumb @model={{@model}} />

    <div class="mb-5">
      <EditContext as |context|>
        <EditQuizContext
          @quiz={{@model.quiz}}
          @answers={{@model.answers}}
          as |quizContext|
        >
          <Sidebar
            @disableScaling={{quizContext.isIsolated}}
            class={{if quizContext.isIsolated "bg-body-tertiary"}}
          >
            <:content>
              <AdminLegacyWarning
                @item={{@model.quiz}}
                @itemType="quiz"
                class="container"
              />

              {{#if quizContext.isIsolated}}
                <IsolatedQuizQuestion
                  @quiz={{@model.quiz}}
                  @answers={{quizContext.answer}}
                  @context={{context}}
                  @isolatedIndex={{quizContext.isolatedIndex}}
                  @isolate={{quizContext.isolate}}
                />
              {{else}}
                <ProgramNavTemplate
                  @program={{@model.program}}
                  @cohort={{@model.cohort}}
                  @parts={{parts}}
                />
                {{#if (eq quizContext.focus "default")}}
                  <CohortNavTemplate
                    @program={{@model.program}}
                    @cohort={{@model.cohort}}
                    @parts={{parts}}
                  >
                    {{#let quizContext.answer as |markedAnswer|}}
                      {{#if (eq quizContext.view "input")}}
                        <QuizInput
                          @quiz={{@model.quiz}}
                          @answers={{markedAnswer}}
                        />
                      {{else}}
                        <QuizOutput
                          @quiz={{@model.quiz}}
                          @answers={{markedAnswer}}
                        />
                      {{/if}}
                    {{/let}}
                  </CohortNavTemplate>
                {{/if}}
              {{/if}}
            </:content>
            <:sidebar>
              <QuizEditMode
                @view={{quizContext.view}}
                @setView={{quizContext.setView}}
                @context={{context}}
              />
              <QuizEditSidebar
                @quiz={{quizContext.quiz}}
                @answers={{quizContext.answer}}
                @context={{context}}
                @view={{quizContext.view}}
                @isolate={{quizContext.isolate}}
              />
            </:sidebar>
          </Sidebar>
        </EditQuizContext>
      </EditContext>
    </div>
  </template>
}

export default ContentQuiz;
